import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FiEye, FiEdit, FiPlay, FiCheckCircle, FiXCircle, FiPauseCircle, FiLoader, FiExternalLink } from 'react-icons/fi';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from "@/lib/utils";

/**
 * Get the health status of a monitor based on its operational state and last run result
 */
function getHealthStatus(monitor) {
    const isRunning = monitor.status?.toLowerCase() === 'running' && monitor.isActive;
    const hasLastRun = monitor.lastRun !== null;
    const lastRunSuccess = monitor.lastRun?.success;
    const isCurrentlyRunning = monitor.lastRun?.isRunning;

    if (isCurrentlyRunning) {
        return {
            label: 'Running',
            color: 'border-violet-200 text-violet-700 dark:border-violet-700 dark:text-violet-300',
            icon: FiLoader,
            showCountdown: false
        };
    }

    if (!isRunning) {
        return {
            label: 'Paused',
            color: 'border-amber-200 text-amber-700 dark:border-amber-700 dark:text-amber-300',
            icon: FiPauseCircle,
            showCountdown: false
        };
    }

    if (!hasLastRun) {
        return {
            label: 'Pending',
            color: 'border-muted-foreground/30 text-muted-foreground',
            icon: FiLoader,
            showCountdown: false
        };
    }

    if (lastRunSuccess) {
        return {
            label: 'Healthy',
            color: 'border-emerald-200 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300',
            icon: FiCheckCircle,
            showCountdown: true
        };
    }

    return {
        label: 'Failing',
        color: 'border-red-200 text-red-700 dark:border-red-700 dark:text-red-300',
        icon: FiXCircle,
        showCountdown: true
    };
}

/**
 * Format the next run time for display
 */
function formatNextRun(msUntilNextRun) {
    if (msUntilNextRun === null || msUntilNextRun === undefined) return null;
    
    if (msUntilNextRun <= 0) {
        return { text: 'now', isOverdue: true };
    }
    
    const minutes = Math.floor(msUntilNextRun / 60000);
    const seconds = Math.floor((msUntilNextRun % 60000) / 1000);
    const padded = (n) => n.toString().padStart(2, '0');
    
    if (minutes < 1) {
        return { text: `${padded(seconds)}s`, isOverdue: false };
    }
    
    if (minutes < 60) {
        return { text: `${padded(minutes)}m ${padded(seconds)}s`, isOverdue: false };
    }
    
    // For longer times, just show hours and minutes
    const hours = Math.floor(minutes / 60);
    const remainingMins = minutes % 60;
    return { text: `${padded(hours)}h ${padded(remainingMins)}m`, isOverdue: false };
}

/**
 * Format elapsed time for display (counts up from 0)
 */
function formatElapsedTime(ms) {
    if (ms === null || ms === undefined || ms < 0) return null;
    
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const padded = (n) => n.toString().padStart(2, '0');
    
    if (minutes < 1) {
        return `${seconds}s`;
    }
    
    if (minutes < 60) {
        return `${minutes}m ${padded(seconds)}s`;
    }
    
    // For longer times, show hours, minutes and seconds
    const hours = Math.floor(minutes / 60);
    const remainingMins = minutes % 60;
    return `${hours}h ${padded(remainingMins)}m ${padded(seconds)}s`;
}

/**
 * Format response time - shows ms or seconds depending on value
 */
function formatResponseTime(responseTimeStr) {
    if (!responseTimeStr) return null;
    
    // Extract the numeric value from strings like "1234ms"
    const match = responseTimeStr.match(/(\d+)ms/);
    if (!match) return responseTimeStr; // Return as-is if format is unexpected
    
    const ms = parseInt(match[1], 10);
    
    // If less than 1000ms, show milliseconds
    if (ms < 1000) {
        return `${ms}ms`;
    }
    
    // If 1000ms or more, show seconds with 2 decimal places
    const seconds = (ms / 1000).toFixed(2);
    return `${seconds}s`;
}

/**
 * Format timestamp as relative time (e.g., "2m ago")
 */
function formatTimestamp(timestampMs) {
    if (!timestampMs) return null;
    
    const now = Date.now();
    const diff = now - timestampMs;
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    // For older dates, show the actual date
    return new Date(timestampMs).toLocaleDateString();
}

export function MonitorListCard({ monitor, onClick, onRunNow, onCountdownExpired, isRefreshing = false }) {
    const navigate = useNavigate();
    
    const healthStatus = getHealthStatus(monitor);
    const StatusIcon = healthStatus.icon;
    
    // Target timestamp for when next run should occur
    const [targetTime, setTargetTime] = useState(() => {
        const ms = monitor.nextRun?.msUntilNextRun;
        return ms != null ? Date.now() + ms : null;
    });

    // Current countdown value (calculated from targetTime)
    const [countdown, setCountdown] = useState(monitor.nextRun?.msUntilNextRun ?? null);

    // Track if we've already notified parent about countdown expiring
    const hasNotifiedRef = useRef(false);
    
    // Elapsed time counter for when monitor is running
    const [elapsedTime, setElapsedTime] = useState(0);
    const runningStartTimeRef = useRef(null);

    // Update countdown every second based on target time
    useEffect(() => {
        if (!monitor.isActive || targetTime === null) return;
        
        const interval = setInterval(() => {
            const remaining = targetTime - Date.now();
            
            // Notify parent when countdown expires
            if (remaining <= 0 && !hasNotifiedRef.current && onCountdownExpired) {
                hasNotifiedRef.current = true;
                onCountdownExpired(monitor.id);
            }
            
            setCountdown(remaining);
        }, 1000);

        return () => clearInterval(interval);
    }, [monitor.isActive, targetTime, monitor.id, onCountdownExpired]);

    // Reset countdown and target time when monitor data changes
    useEffect(() => {
        // Don't reset countdown while monitor is being refreshed/polled
        // This prevents the countdown from jumping when server returns the next scheduled run time
        if (isRefreshing) {
            return;
        }
        
        const ms = monitor.nextRun?.msUntilNextRun;
        const newTargetTime = ms != null ? Date.now() + ms : null;
        
        setTargetTime(newTargetTime);
        setCountdown(ms ?? null);
        
        // Reset notification flag when countdown resets to a positive value
        if (ms > 0) {
            hasNotifiedRef.current = false;
        }
    }, [monitor.nextRun?.msUntilNextRun, isRefreshing]);

    const nextRunInfo = formatNextRun(countdown);
    const isFailing = healthStatus.label === 'Failing';
    
    // Only show "Running" when parent is actively refreshing (polling or user-triggered)
    const showCheckingState = isRefreshing;
    
    // Track elapsed time when monitor is running
    useEffect(() => {
        if (showCheckingState) {
            // Start counting or update with server time
            const isActuallyRunning = monitor.lastRun?.isRunning === true;
            const serverStartTime = isActuallyRunning ? monitor.lastRun?.startTimeTimestamp : null;
            
            if (!runningStartTimeRef.current) {
                // First time - use server time if available, otherwise use now
                runningStartTimeRef.current = serverStartTime || Date.now();
                const initialElapsed = Date.now() - runningStartTimeRef.current;
                setElapsedTime(initialElapsed);
            } else if (serverStartTime && serverStartTime !== runningStartTimeRef.current) {
                // Update with actual server time when it arrives
                runningStartTimeRef.current = serverStartTime;
            }
            
            const interval = setInterval(() => {
                if (runningStartTimeRef.current) {
                    setElapsedTime(Date.now() - runningStartTimeRef.current);
                }
            }, 100); // Update every 100ms for smooth display
            
            return () => clearInterval(interval);
        } else {
            // Reset when not running
            runningStartTimeRef.current = null;
            setElapsedTime(0);
        }
    }, [showCheckingState, monitor.lastRun?.startTimeTimestamp, monitor.lastRun?.isRunning]);
    
    const handleViewDetails = (e) => {
        e.stopPropagation();
        navigate(`/monitors/${monitor.id}`);
    };

    const handleEdit = (e) => {
        e.stopPropagation();
        navigate(`/monitors/${monitor.id}/edit`);
    };

    const handleRunNow = (e) => {
        e.stopPropagation();
        if (onRunNow) {
            onRunNow(monitor);
        }
    };

    const handleViewLastRun = (e) => {
        e.stopPropagation();
        if (monitor.lastRun?.runId) {
            navigate(`/monitors/${monitor.id}/runs/${monitor.lastRun.runId}`);
        }
    };

    const handleOpenUrl = (e) => {
        e.stopPropagation();
        if (monitor.url) {
            window.open(monitor.url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <TooltipProvider delayDuration={300}>
            <Card 
                className={cn(
                    "transition-all duration-200 hover:shadow-md relative h-[190px]",
                    isFailing && "border-red-300 dark:border-red-700"
                )}
            >
                <CardContent className="p-4">
                    <div className="space-y-2.5">
                        {/* Header with name and combined status badge */}
                        <div className="flex items-start justify-between gap-3">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <h3 
                                        onClick={handleViewDetails}
                                        className="text-lg font-semibold text-foreground truncate hover:text-violet-600 dark:hover:text-violet-500 transition-colors min-w-0 cursor-pointer"
                                    >
                                        {monitor.name}
                                    </h3>
                                </TooltipTrigger>
                                <TooltipContent side="top" align="start">
                                    <p>{monitor.name}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Badge 
                                        variant="outline"
                                        onClick={monitor.lastRun?.runId ? handleViewLastRun : undefined}
                                        className={cn(
                                            "text-xs px-2.5 py-1 flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap transition-all duration-300",
                                            healthStatus.color,
                                            monitor.lastRun?.runId && "cursor-pointer hover:opacity-80"
                                        )}
                                    >
                                        {showCheckingState ? (
                                            <>
                                                <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin" />
                                                <span>Running</span>
                                                {elapsedTime > 0 && (
                                                    <>
                                                        <span className="opacity-50">·</span>
                                                        <span className="font-mono">
                                                            {formatElapsedTime(elapsedTime)}
                                                        </span>
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <StatusIcon className="h-3 w-3 flex-shrink-0" />
                                                <span>{healthStatus.label}</span>
                                                {healthStatus.showCountdown && nextRunInfo && (
                                                    <>
                                                        <span className="opacity-50">·</span>
                                                        <span className="font-mono">
                                                            {nextRunInfo.text}
                                                        </span>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                    <div className="space-y-0.5">
                                        <p className="font-semibold">{healthStatus.label}</p>
                                        <p className="text-xs opacity-90">
                                            Runs every {monitor.interval}
                                        </p>
                                        {healthStatus.showCountdown && nextRunInfo && !showCheckingState && (
                                            <p className="text-xs opacity-90">
                                                Next check in {nextRunInfo.text}
                                            </p>
                                        )}
                                        {monitor.lastRun && (
                                            <p className="text-xs opacity-70">
                                                Last: {monitor.lastRun.success ? '✓' : '✗'} {monitor.lastRun.startTime}
                                            </p>
                                        )}
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </div>

                        {/* Type & Organization */}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{monitor.actionTypeName}</span>
                            <div className="flex items-center min-w-0">
                                <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mr-1.5 flex-shrink-0"></div>
                                <span className="truncate">{monitor.organizationName}</span>
                            </div>
                        </div>

                        {/* URL */}
                        {monitor.urlSimplified && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div 
                                        onClick={handleOpenUrl}
                                        className="group/url text-xs text-muted-foreground hover:text-violet-600 dark:hover:text-violet-400 hover:underline transition-colors cursor-pointer flex items-center gap-1.5 w-fit max-w-full"
                                    >
                                        <span className="truncate">{monitor.urlSimplified}</span>
                                        <FiExternalLink className="h-3 w-3 flex-shrink-0 opacity-0 group-hover/url:opacity-100 transition-opacity" />
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" align="start" className="max-w-md break-all">
                                    <p>{monitor.url}</p>
                                </TooltipContent>
                            </Tooltip>
                        )}

                        {/* Last run info */}
                        {monitor.lastRun ? (
                            <div className="flex items-center justify-between text-xs gap-2">
                                <div 
                                    onClick={handleViewLastRun}
                                    className="cursor-pointer flex items-center gap-1.5 transition-colors group/lastrun"
                                >
                                    <span className={cn(
                                        "font-medium group-hover/lastrun:underline",
                                        monitor.lastRun.isRunning
                                            ? 'text-violet-600 dark:text-violet-400 group-hover/lastrun:text-violet-700 dark:group-hover/lastrun:text-violet-300'
                                            : monitor.lastRun.success 
                                                ? 'text-emerald-600 dark:text-emerald-400 group-hover/lastrun:text-emerald-700 dark:group-hover/lastrun:text-emerald-300' 
                                                : 'text-red-600 dark:text-red-400 group-hover/lastrun:text-red-700 dark:group-hover/lastrun:text-red-300'
                                    )}>
                                        {monitor.lastRun.isRunning 
                                            ? 'Check in progress...' 
                                            : monitor.lastRun.success 
                                                ? 'Last check passed' 
                                                : 'Last check failed'}
                                    </span>
                                    {monitor.lastRun.startTimeTimestamp && (
                                        <>
                                            <span className="opacity-40">•</span>
                                            <span className="text-muted-foreground">
                                                {formatTimestamp(monitor.lastRun.startTimeTimestamp)}
                                            </span>
                                        </>
                                    )}
                                </div>
                                {monitor.lastRun.responseTime && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span 
                                                onClick={handleViewLastRun}
                                                className={cn(
                                                    "text-muted-foreground font-mono transition-opacity duration-300 flex-shrink-0 cursor-pointer hover:text-foreground hover:underline",
                                                    showCheckingState && "opacity-30"
                                                )}
                                            >
                                                {formatResponseTime(monitor.lastRun.responseTime)}
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                            <p className="text-xs">
                                                {monitor.actionType === 'browser_agent' 
                                                    ? 'Average Page Speed'
                                                    : 'Response Time'}
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                        ) : (
                            <div className="text-xs text-muted-foreground">
                                No checks yet
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex justify-between items-center pt-3">
                            <div className="flex space-x-2">
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={handleViewDetails}
                                    className="flex items-center justify-center text-xs h-7"
                                >
                                    <span>View Details</span>
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={handleEdit}
                                    className="flex items-center justify-center text-xs h-7"
                                >
                                    <span>Edit</span>
                                </Button>
                            </div>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={handleRunNow}
                                        disabled={isRefreshing}
                                        className="flex items-center justify-center text-xs h-7 px-3"
                                    >
                                        <FiPlay className="h-3 w-3" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    <p>Run once</p>
                                </TooltipContent>
                            </Tooltip>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </TooltipProvider>
    );
}

export default MonitorListCard;
